<template>
	<div class="checkout-page-container">
		<div class="navbar-white">
			<Navbar class="desktop-center" :logoSrc="$logoSrc" />
		</div>
		<div class="checkout-page-content navbar-page desktop-center">
			<div class="checkout-content sk-scrollbar" ref="checkoutContent">
				<div class="sk-widget-title">Customer Information</div>
				<div class="checkout-subtitle">
					Ensure your order finds you as fast as possible!
				</div>
				<LockerTypeOption
					v-if="
						cartDelivery == 1 &&
						deliveryMode == deliveryModes.LOCKER
					"
					@deliveryModeSelected="onDeliveryModeSelected"
					ref="lockerTypeOption"
				/>
				<MultipleTypeOptions
					v-else
					ref="multipleTypeOptions"
					@deliveryModeSelected="onDeliveryModeSelected"
				/>
				<DefaultContactForm
					ref="defaultContactForm"
					:container="$refs.checkoutContent"
					:paymentMethod="paymentMethodSelected"
				/>
				<div class="sk-widget-title">Payment</div>
				<div class="checkout-subtitle">
					Choose from multiple convenient options!
				</div>
				<!-- <PaymentOptions
					ref="paymentOptions"
					v-model="paymentMethodSelected"
					:options="paymentMethods"
					@methodSelected="paymentMethodClicked"
				/> -->
				<form id="payment-form" ref="cardOption">
					<div id="card-container"></div>
					<button
						id="card-button"
						type="button"
						style="display: none"
					></button>
				</form>
				<div id="payment-status-container"></div>
			</div>
			<FloatingCartSummary
				ref="cartSummary"
				@checkout="checkoutClicked"
			/>
			<Footer @checkout="checkoutClicked" />
		</div>
		<ItemModal ref="itemModal" />
	</div>
</template>

<style scoped>
.checkout-page-content {
	display: flex;
}

.sk-widget-alt {
	overflow: visible;
}

.checkout-cc-form-container {
	margin: -15px;
	background-color: var(--sk-grey-bg);
}

.checkout-cc-form {
	background-color: var(--sk-grey-bg);
	position: relative;
	size: 100%;
}

.sk-widget-title {
	margin-top: 30px;
	margin-bottom: 0;
}

.sk-widget-title,
.checkout-subtitle {
	margin-left: 24px;
}

.checkout-content {
	margin: 0 5px;
	flex: 1;
	overflow-y: auto;
	overflow-x: hidden;
	padding-bottom: 70px;
}

.checkout-page-container >>> .alcohol-disclaimer {
	align-items: center;
	background: var(--sk-grey-bg);
	padding: 15px;
	margin: 20px 0;
	border-radius: 5px;
	border: 1px solid var(--gR-white2);
	font-size: 10px;
}

.checkout-page-container >>> .alcohol-disclaimer i {
	margin-right: 20px;
}

.checkout-content >>> .checkout-additional-info {
	padding: 0;
}
</style>

<script>
import { NotificationType } from "@/enums/NotificationType"
import { eventBus, EventBusEvents } from "@/eventBus"
import { PaymentType } from "@reef-os/utils/enums/paymentType"
import mobileResponsivenessMixin from "@/mixins/mobile-responsiveness-mixin"
import squareFormMixin from "@/mixins/square-form-mixin"
import checkoutMixin from "@/mixins/checkout-mixin"
import loaderMixin from "@/mixins/loader-mixin"
import Navbar from "@/components/navbars/Navbar.vue"
import FloatingCartSummary from "@/components/common/FloatingCartSummary.vue"
import PaymentOptions from "@/components/checkout/PaymentOptions.vue"
import DefaultContactForm from "@/components/checkout/DefaultContactForm.vue"
import Footer from "@/components/Footer.vue"
import ItemModal from "@/components/modals/ItemModal.vue"
import LockerTypeOption from "@/components/checkout/LockerTypeOption.vue"
import MultipleTypeOptions from "@/components/checkout/MultipleTypeOptions.vue"
import { DeliveryMode } from "@reef-os/utils/enums/deliveryMode"

export default {
	name: "AirportCheckout",
	mixins: [
		loaderMixin,
		mobileResponsivenessMixin,
		squareFormMixin,
		checkoutMixin
	],
	components: {
		Navbar,
		FloatingCartSummary,
		PaymentOptions,
		DefaultContactForm,
		Footer,
		ItemModal,
		LockerTypeOption,
		MultipleTypeOptions
	},
	data: function () {
		return {
			isDeliveryModeSelected: false,
			mobileThresholdWidth: 900,
			paymentMethodSelected: null,
			deliveryModes: DeliveryMode
		}
	},
	computed: {
		showCreditCardInfo() {
			return this.paymentMethodSelected == PaymentType.CREDIT_CARD
		},
		paymentMethodObserver() {
			return this.allowedPaymentMethods && this.paymentMethods
		},
		cartDelivery() {
			if (this.$store.getters.cartDeliveryModes) {
				return this.$store.getters.cartDeliveryModes.length
			}
		},
		deliveryMode() {
			return this.$store.getters.deliveryMode
		}
	},
	methods: {
		onDeliveryModeSelected() {
			this.isDeliveryModeSelected = true
		},
		methodsSupported(supportedMethods) {
			if (supportedMethods.applePay) {
				this.applePaySupported = supportedMethods.applePay === true
			}
			if (supportedMethods.googlePay) {
				this.googlePaySupported = supportedMethods.googlePay === true
			}
		},
		paymentMethodClicked(id) {
			this.$store.dispatch("sendMixpanelEvent", {
				msg: "Payment Method Set",
				data: {
					id: id
				}
			})
			this.paymentMethodSelected = id
		},
		focusInput(input) {
			if (this.$refs.checkoutContent) {
				this.$refs.checkoutContent.scroll({
					top:
						input.$el.offsetTop -
						this.$refs.checkoutContent.offsetTop -
						15,
					behavior: "smooth"
				})
				setTimeout(() => {
					input.focusInput()
				}, 500)
			} else {
				input.focusInput()
			}
		},
		checkoutClicked() {
			const validContactForm = !this.$refs.defaultContactForm.validate()
			if (!validContactForm) {
				return false
			}

			if (!this.isDeliveryModeSelected) {
				eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
					message: "Please select a delivery type",
					type: NotificationType.WARNING
				})
				return false
			}

			if (
				this.deliveryMode == DeliveryMode.LOCKER &&
				this.$store.state.is_accessible_pickup == null
			) {
				if (this.$refs.lockerTypeOption) {
					this.$refs.lockerTypeOption.openFoodhallPickupModal()
				} else {
					this.$refs.multipleTypeOptions.openFoodhallPickupModal()
				}
				eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
					message: "Please select a locker type",
					type: NotificationType.WARNING
				})
				return false
			}

			if (
				this.deliveryMode == DeliveryMode.DIRECT_TO_LOCATION &&
				!this.$store.state.businessDropoffLocationId
			) {
				this.$refs.multipleTypeOptions.openGateDeliveryModal()
				eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
					message: "Please select a gate",
					type: NotificationType.WARNING
				})
				return false
			}

			if (this.$store.getters.totalCheckoutPrice <= 0) {
				if (!this.$store.state.inPurchaseState) {
					this.$store.dispatch("sendMixpanelEvent", {
						msg: "Click Purchase",
						data: {
							type: "Voucher"
						}
					})
					this.toggleLoader(true)
					this.$store.dispatch("processVoucherOrder")
				}
				return
			}
			switch (this.paymentMethodSelected) {
				case PaymentType.CREDIT_CARD: {
					this.clickPurchase()
					break
				}
				case PaymentType.GOOGLE_PAY: {
					this.$refs.paymentOptions.$refs.googlePayBtn.click()
					break
				}
				case PaymentType.APPLE_PAY: {
					this.$refs.paymentOptions.$refs.applePayBtn.click()
					break
				}
				default: {
					eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
						message: "Please select a payment option",
						type: NotificationType.WARNING
					})
					throw "Missing payment option"
				}
			}
		},
		onCardNonceError(errors) {
			let errorMsg = errors[0].message
			eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
				message: errorMsg,
				type: NotificationType.ERROR
			})
		},
		inputEventReceived(inputEvent) {
			switch (inputEvent.eventType) {
				case "errorClassAdded":
					this.toggleLoader(false)
					break
			}
		},
		beforeRouteUpdate: function (to) {
			this.detectItemModalRoute(to)
		}
	},
	watch: {
		paymentMethodObserver: {
			immediate: true,
			handler() {
				this.setDefaultPaymentMethod()
			}
		}
	}
}
</script>
